import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import DateTime24Input from '../components/DateTime24Input';
import { format } from 'date-fns';
import { FiCalendar, FiTool, FiLayers, FiEdit2, FiTrash2, FiArrowLeft, FiUser, FiUsers, FiPlus, FiTarget, FiX, FiLink, FiPaperclip, FiDownload, FiEye } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const TaskDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    deadline: '',
    priority: 'medium',
    tools: [],
    technologies: [],
    assignedTo: '',
    links: [],
  });
  const [editLinkInput, setEditLinkInput] = useState({ url: '', label: '' });
  const [previewAttachment, setPreviewAttachment] = useState({ url: null, type: null, name: '' });
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchTask();
  }, [id]);

  useEffect(() => {
    return () => {
      if (previewAttachment.url) window.URL.revokeObjectURL(previewAttachment.url);
    };
  }, [previewAttachment.url]);

  useEffect(() => {
    if (task && task.isTeamTask) {
      fetchSubtasks();
    }
    if (task) {
      // Fetch team members if task has a team, or all users if user can manage and task doesn't have team
      if (task.team) {
        fetchTeamMembers();
      } else if (user && (user.role === 'admin' || user.role === 'productManager' || user.role === 'teamLead')) {
        // Fetch all users if task doesn't have a team but user can assign
        fetchTeamMembers();
      }
      
      // Initialize edit form data when task loads
      setEditFormData({
        title: task.title || '',
        description: task.description || '',
        deadline: task.deadline ? new Date(task.deadline).toISOString().slice(0, 16) : '',
        priority: task.priority || 'medium',
        tools: task.tools || [],
        technologies: task.technologies || [],
        assignedTo: task.assignedTo?._id || task.assignedTo || '',
        links: task.links && task.links.length ? task.links.map((l) => ({ url: l.url || '', label: l.label || '' })) : [],
      });
    }
  }, [task, user]);

  const fetchTask = async () => {
    try {
      const response = await axios.get(`/tasks/${id}`);
      setTask(response.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch task');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubtasks = async () => {
    setLoadingSubtasks(true);
    try {
      const response = await axios.get(`/tasks/${id}/subtasks`);
      setSubtasks(response.data);
    } catch (error) {
      console.error('Error fetching subtasks:', error);
    } finally {
      setLoadingSubtasks(false);
    }
  };

  const fetchTeamMembers = async () => {
    if (!task?.team) {
      // If no team, fetch all users (for admins/PMs/team leads)
      if (user?.role === 'admin' || user?.role === 'productManager' || user?.role === 'teamLead') {
        try {
          const response = await axios.get('/users');
          setAllUsers(response.data);
        } catch (error) {
          console.error('Error fetching users:', error);
        }
      }
      return;
    }
    try {
      const teamId = task.team._id || task.team;
      const response = await axios.get(`/teams/${teamId}`);
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await axios.put(`/tasks/${id}`, { status: newStatus });
      setTask(response.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update task');
    } finally {
      setUpdating(false);
    }
  };

  const handleProgressChange = async (newProgress) => {
    setUpdating(true);
    try {
      const response = await axios.put(`/tasks/${id}`, { progress: newProgress });
      setTask(response.data);
      
      // If this is a subtask, refresh parent task to show updated progress
      if (task.parentTask) {
        // Fetch parent task to get updated progress
        try {
          const parentResponse = await axios.get(`/tasks/${task.parentTask._id || task.parentTask}`);
          // Optionally update parent task display if needed
        } catch (err) {
          console.error('Error fetching parent task:', err);
        }
      }
      
      // If this is a team task, refresh subtasks to show updated progress
      if (task.isTeamTask) {
        await fetchSubtasks();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update progress');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await axios.delete(`/tasks/${id}`);
      navigate('/dashboard');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete task');
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError('');
    
    try {
      const updateData = {
        ...editFormData,
        deadline: new Date(editFormData.deadline).toISOString(),
        assignedTo: editFormData.assignedTo || undefined,
      };
      
      const response = await axios.put(`/tasks/${id}`, updateData);
      setTask(response.data);
      setIsEditing(false);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update task');
    } finally {
      setUpdating(false);
    }
  };

  const addEditTool = (tool) => {
    if (tool && !editFormData.tools.includes(tool)) {
      setEditFormData({
        ...editFormData,
        tools: [...editFormData.tools, tool],
      });
    }
  };

  const removeEditTool = (tool) => {
    setEditFormData({
      ...editFormData,
      tools: editFormData.tools.filter((t) => t !== tool),
    });
  };

  const addEditTechnology = (tech) => {
    if (tech && !editFormData.technologies.includes(tech)) {
      setEditFormData({
        ...editFormData,
        technologies: [...editFormData.technologies, tech],
      });
    }
  };

  const removeEditTechnology = (tech) => {
    setEditFormData({
      ...editFormData,
      technologies: editFormData.technologies.filter((t) => t !== tech),
    });
  };

  const addEditLink = () => {
    const url = (editLinkInput.url || '').trim();
    if (!url) return;
    setEditFormData({
      ...editFormData,
      links: [...(editFormData.links || []), { url, label: (editLinkInput.label || '').trim() }],
    });
    setEditLinkInput({ url: '', label: '' });
  };

  const removeEditLink = (index) => {
    setEditFormData({
      ...editFormData,
      links: editFormData.links.filter((_, i) => i !== index),
    });
  };

  const getAttachmentPreviewType = (att) => {
    const name = (att.originalName || att.path || '').toLowerCase();
    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return 'image';
    if (/\.pdf$/i.test(name)) return 'pdf';
    return null;
  };

  const handleAttachmentDownload = async (att) => {
    try {
      const response = await axios.get(`/tasks/${id}/attachments/${att.path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.originalName || att.path;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || 'Download failed');
    }
  };

  const handleAttachmentPreview = async (att) => {
    const type = getAttachmentPreviewType(att);
    if (!type) {
      handleAttachmentDownload(att);
      return;
    }
    setPreviewLoading(true);
    setPreviewAttachment({ url: null, type: null, name: '' });
    try {
      const response = await axios.get(`/tasks/${id}/attachments/${att.path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      setPreviewAttachment({ url, type, name: att.originalName || att.path });
    } catch (err) {
      setError(err.response?.data?.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewAttachment.url) window.URL.revokeObjectURL(previewAttachment.url);
    setPreviewAttachment({ url: null, type: null, name: '' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'in-progress':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'on-hold':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default:
        return 'bg-dark-300 text-dark-100 border-dark-300';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500/20 text-red-400';
      case 'high':
        return 'bg-orange-500/20 text-orange-400';
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-blue-500/20 text-blue-400';
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-300"></div>
        </div>
      </>
    );
  }

  if (!task) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Task not found</h1>
            <Link to="/dashboard" className="text-purple-200 hover:text-purple-100">
              Go back to dashboard
            </Link>
          </div>
        </div>
      </>
    );
  }

  // Determine edit permissions
  // Users cannot edit major tasks (parent team tasks), only their assigned subtasks
  const isMajorTask = task.isTeamTask && !task.parentTask;
  const isAdmin = user?.role === 'admin';
  const isTeamLead = user?.role === 'teamLead';
  
  // Check if team lead is the leader of the task's team
  // For team leads, if task has a team, assume they can edit (backend will verify)
  // This allows the UI to show edit buttons, backend will reject if not authorized
  const isTeamLeadOfTaskTeam = isTeamLead && task.team;
  
  // Only Admin can edit any task
  // Team leads can edit tasks in their teams (backend will verify actual team ownership)
  // Regular users can edit their own tasks (but not major team tasks)
  // Product Manager can only VIEW, not edit
  const canEdit = isAdmin || // Only Admin can edit any task
    isTeamLeadOfTaskTeam || // Team leads can edit tasks in their teams (backend will verify)
    (isMajorTask ? false : // Users cannot edit major tasks
      (task.createdBy._id === user?._id || // Users can edit tasks they created
        (task.assignedTo && (task.assignedTo._id === user?._id || task.assignedTo === user?._id)))); // Or tasks assigned to them
  
  // Only Admin can delete any task
  // Team leads can delete tasks in their teams
  // Regular users can delete their own tasks (but not major team tasks)
  // Product Manager can only VIEW, not delete
  const canDelete = isAdmin || // Only Admin can delete any task
    isTeamLeadOfTaskTeam || // Team leads can delete tasks in their teams
    (isMajorTask ? false : // Users cannot delete major tasks
      task.createdBy._id === user?._id); // Users can delete tasks they created

  return (
    <div className="min-h-screen bg-background relative">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center space-x-2 text-dark-100 hover:text-purple-100 mb-6 transition-colors"
        >
          <FiArrowLeft />
          <span>Back to Dashboard</span>
        </Link>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="bg-stroke border border-stroke rounded-lg p-8">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              {isEditing ? (
                <input
                  type="text"
                  name="title"
                  value={editFormData.title}
                  onChange={handleEditChange}
                  className="text-3xl font-bold text-white mb-4 w-full bg-background border border-dark-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300"
                  placeholder="Task Title"
                />
              ) : (
                <h1 className="text-3xl font-bold text-white mb-4">{task.title}</h1>
              )}
              <div className="flex flex-wrap gap-3">
                <span className={`px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(task.status)}`}>
                  {task.status}
                </span>
                {isEditing ? (
                  <select
                    name="priority"
                    value={editFormData.priority}
                    onChange={handleEditChange}
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${getPriorityColor(editFormData.priority)} bg-background border border-dark-300`}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                ) : (
                  <span className={`px-3 py-1 rounded-lg text-sm font-medium ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                )}
              </div>
            </div>
            {canEdit && (
              <div className="flex space-x-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="p-2 bg-dark-300 text-white rounded-lg hover:bg-dark-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEditSubmit}
                      disabled={updating}
                      className="p-2 bg-purple-300 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      Save
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-2 bg-purple-300/20 text-purple-200 rounded-lg hover:bg-purple-300/30 transition-colors"
                  >
                    <FiEdit2 />
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={handleDelete}
                    className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                  >
                    <FiTrash2 />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mb-6">
            <h2 className="text-lg font-semibold text-white mb-2">Description</h2>
            {isEditing ? (
              <textarea
                name="description"
                value={editFormData.description}
                onChange={handleEditChange}
                rows="4"
                className="w-full px-4 py-3 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none"
                placeholder="Task description"
              />
            ) : (
              <p className="text-dark-100 whitespace-pre-wrap">{task.description || 'No description'}</p>
            )}
          </div>

          {(task.links && task.links.length > 0) || isEditing ? (
            <div className="mb-6">
              <div className="flex items-center text-dark-100 mb-3">
                <FiLink className="mr-2" />
                <span className="font-medium">Links</span>
              </div>
              {isEditing ? (
                <div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(editFormData.links || []).map((link, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-2 bg-purple-300/20 text-purple-200 px-3 py-1.5 rounded-lg text-sm"
                      >
                        {link.label || link.url}
                        <button type="button" onClick={() => removeEditLink(i)} className="hover:text-red-400">
                          <FiX size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <input
                      type="url"
                      value={editLinkInput.url}
                      onChange={(e) => setEditLinkInput((p) => ({ ...p, url: e.target.value }))}
                      placeholder="URL"
                      className="flex-1 min-w-[180px] px-4 py-2 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300"
                    />
                    <input
                      type="text"
                      value={editLinkInput.label}
                      onChange={(e) => setEditLinkInput((p) => ({ ...p, label: e.target.value }))}
                      placeholder="Label (optional)"
                      className="w-32 px-4 py-2 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300"
                    />
                    <button type="button" onClick={addEditLink} className="px-4 py-2 bg-purple-300 text-white rounded-lg hover:opacity-90">
                      Add
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {task.links.map((link, i) => (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-purple-300/20 text-purple-200 hover:bg-purple-300/30 px-3 py-1.5 rounded-lg text-sm truncate max-w-[280px]"
                    >
                      {link.label || link.url}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {task.attachments && task.attachments.length > 0 ? (
            <div className="mb-6">
              <div className="flex items-center text-dark-100 mb-3">
                <FiPaperclip className="mr-2" />
                <span className="font-medium">Attachments</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {task.attachments.map((att, i) => {
                  const canPreview = getAttachmentPreviewType(att);
                  return canPreview ? (
                    <div
                      key={i}
                      className="inline-flex items-center rounded-lg overflow-hidden bg-dark-300"
                    >
                      <button
                        type="button"
                        onClick={() => handleAttachmentPreview(att)}
                        disabled={previewLoading}
                        className="inline-flex items-center gap-2 px-3 py-2 text-white hover:bg-dark-200 transition-colors disabled:opacity-50"
                      >
                        <FiEye size={14} />
                        <span className="truncate max-w-[140px]">{att.originalName || att.path}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAttachmentDownload(att)}
                        className="inline-flex items-center p-2 text-white hover:bg-dark-200 transition-colors border-l border-dark-200"
                        title="Download"
                      >
                        <FiDownload size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleAttachmentDownload(att)}
                      className="inline-flex items-center gap-2 bg-dark-300 hover:bg-dark-200 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                    >
                      <FiDownload size={14} />
                      <span className="truncate max-w-[200px]">{att.originalName || att.path}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {previewAttachment.url && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
              onClick={closePreview}
              role="dialog"
              aria-modal="true"
              aria-label="File preview"
            >
              <div
                className="relative max-w-[90vw] max-h-[90vh] bg-stroke rounded-lg overflow-hidden shadow-xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-4 py-2 border-b border-stroke bg-background">
                  <span className="text-white truncate max-w-[70vw]">{previewAttachment.name}</span>
                  <button
                    type="button"
                    onClick={closePreview}
                    className="p-2 text-dark-100 hover:text-white rounded-lg hover:bg-dark-300 transition-colors"
                    aria-label="Close preview"
                  >
                    <FiX size={24} />
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-4 flex items-center justify-center min-h-[200px]">
                  {previewAttachment.type === 'image' && (
                    <img
                      src={previewAttachment.url}
                      alt={previewAttachment.name}
                      className="max-w-full max-h-[80vh] object-contain"
                    />
                  )}
                  {previewAttachment.type === 'pdf' && (
                    <iframe
                      src={previewAttachment.url}
                      title={previewAttachment.name}
                      className="w-full min-h-[70vh] rounded border-0"
                      style={{ minWidth: 'min(800px, 90vw)' }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <div className="flex items-center text-dark-100 mb-2">
                <FiCalendar className="mr-2 text-white" />
                <span className="font-medium">Deadline</span>
              </div>
              {isEditing ? (
                <DateTime24Input
                  value={editFormData.deadline}
                  onChange={(v) => setEditFormData((prev) => ({ ...prev, deadline: v }))}
                  className="w-full"
                />
              ) : (
                <p className="text-white">{format(new Date(task.deadline), 'dd MMM yyyy, HH:mm')}</p>
              )}
            </div>

            <div>
              <div className="flex items-center text-dark-100 mb-2">
                <FiUser className="mr-2" />
                <span className="font-medium">Created By</span>
              </div>
              <p className="text-white">{task.createdBy.name}</p>
            </div>
            
            <div>
              <div className="flex items-center text-dark-100 mb-2">
                <FiUsers className="mr-2" />
                <span className="font-medium">Assigned To</span>
              </div>
              {isEditing && canEdit ? (
                <select
                  name="assignedTo"
                  value={editFormData.assignedTo}
                  onChange={handleEditChange}
                  className="w-full px-4 py-3 bg-background border border-dark-300 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-300"
                >
                  <option value="">Not Assigned</option>
                  {task.team && teamMembers.length > 0 ? (
                    teamMembers.map((member) => {
                      const memberUser = member.user || member;
                      return (
                        <option key={memberUser._id} value={memberUser._id}>
                          {memberUser.name} {member.role && `(${member.role})`}
                        </option>
                      );
                    })
                  ) : allUsers.length > 0 ? (
                    allUsers.map((userItem) => (
                      <option key={userItem._id} value={userItem._id}>
                        {userItem.name} ({userItem.role})
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Loading users...</option>
                  )}
                </select>
              ) : (
                <p className="text-white">
                  {task.assignedTo ? task.assignedTo.name : 'Not Assigned'}
                </p>
              )}
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center text-dark-100 mb-3">
              <FiTool className="mr-2" />
              <span className="font-medium">Tools</span>
            </div>
            {isEditing ? (
              <div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {editFormData.tools.map((tool, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center space-x-1 bg-purple-300/20 text-purple-200 px-3 py-1 rounded-lg text-sm"
                    >
                      <span>{tool}</span>
                      <button
                        type="button"
                        onClick={() => removeEditTool(tool)}
                        className="hover:text-red-400"
                      >
                        <FiX size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addEditTool(e.target.value.trim());
                        e.target.value = '';
                      }
                    }}
                    placeholder="Add a tool"
                    className="flex-1 px-4 py-2 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {task.tools && task.tools.length > 0 ? (
                  task.tools.map((tool, idx) => (
                    <span
                      key={idx}
                      className="bg-purple-300/20 text-purple-200 px-3 py-1 rounded-lg text-sm"
                    >
                      {tool}
                    </span>
                  ))
                ) : (
                  <span className="text-dark-100 text-sm">No tools specified</span>
                )}
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className="flex items-center text-dark-100 mb-3">
              <FiLayers className="mr-2" />
              <span className="font-medium">Technologies</span>
            </div>
            {isEditing ? (
              <div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {editFormData.technologies.map((tech, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center space-x-1 bg-purple-200/20 text-purple-100 px-3 py-1 rounded-lg text-sm"
                    >
                      <span>{tech}</span>
                      <button
                        type="button"
                        onClick={() => removeEditTechnology(tech)}
                        className="hover:text-red-400"
                      >
                        <FiX size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addEditTechnology(e.target.value.trim());
                        e.target.value = '';
                      }
                    }}
                    placeholder="Add a technology"
                    className="flex-1 px-4 py-2 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {task.technologies && task.technologies.length > 0 ? (
                  task.technologies.map((tech, idx) => (
                    <span
                      key={idx}
                      className="bg-purple-200/20 text-purple-100 px-3 py-1 rounded-lg text-sm"
                    >
                      {tech}
                    </span>
                  ))
                ) : (
                  <span className="text-dark-100 text-sm">No technologies specified</span>
                )}
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-white">Progress</span>
              <span className="text-white font-semibold">{task.progress}%</span>
            </div>
            <div className="w-full bg-dark-300 rounded-full h-3 mb-2">
              <div
                className="bg-gradient-to-r from-purple-200 to-purple-300 h-3 rounded-full transition-all duration-300"
                style={{ width: `${task.progress}%` }}
              />
            </div>
            {canEdit && (
              <div className="flex gap-2 mt-2">
                {[0, 25, 50, 75, 100].map((progress) => (
                  <button
                    key={progress}
                    onClick={() => handleProgressChange(progress)}
                    disabled={updating}
                    className="px-3 py-1 bg-dark-300 text-dark-100 rounded text-sm hover:bg-purple-300/20 hover:text-purple-200 transition-colors disabled:opacity-50"
                  >
                    {progress}%
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Subtasks Section (for Team Tasks) */}
          {task.isTeamTask && (
            <div className="border-t border-dark-300 pt-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <FiUsers className="mr-2" />
                  Subtasks ({subtasks.length})
                </h3>
                 {canEdit && (
                   <Link
                     to={`/create-task?team=${task.team?._id || task.team}&parentTask=${task._id}`}
                     className="flex items-center space-x-2 text-purple-200 hover:text-purple-100 transition-colors"
                   >
                     <FiPlus />
                     <span>Add Subtask</span>
                   </Link>
                 )}
              </div>

              {loadingSubtasks ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-300 mx-auto"></div>
                </div>
              ) : subtasks.length === 0 ? (
                <div className="bg-background border border-dark-300 rounded-lg p-8 text-center">
                  <FiTarget className="text-4xl text-dark-300 mx-auto mb-3" />
                   <p className="text-dark-100 mb-4">No subtasks yet</p>
                   {canEdit && (
                     <Link
                       to={`/create-task?team=${task.team?._id || task.team}&parentTask=${task._id}`}
                       className="inline-flex items-center space-x-2 bg-purple-300 text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                     >
                       <FiPlus />
                       <span>Create First Subtask</span>
                     </Link>
                   )}
                </div>
              ) : (
                <div className="space-y-3">
                  {subtasks.map((subtask) => {
                    const isAssigned = subtask.assignedTo?._id || subtask.assignedTo;
                    return (
                      <Link
                        key={subtask._id}
                        to={`/task/${subtask._id}`}
                        className="block p-4 bg-background border border-dark-300 rounded-lg hover:border-purple-300 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="text-white font-semibold">{subtask.title}</h4>
                            {isAssigned && (
                              <div className="flex items-center text-dark-100 text-sm mt-1">
                                <FiUser className="mr-1" />
                                <span>Assigned to: {subtask.assignedTo?.name || 'Team Member'}</span>
                              </div>
                            )}
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(subtask.status)}`}>
                            {subtask.status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex-1 bg-dark-300 rounded-full h-2 mr-3">
                            <div
                              className="bg-gradient-to-r from-purple-200 to-purple-300 h-2 rounded-full transition-all"
                              style={{ width: `${subtask.progress || 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-white font-semibold">{subtask.progress || 0}%</span>
                        </div>
                      </Link>
                    );
                  })}
                  
                  {/* Team Progress Summary */}
                  <div className="bg-purple-300/10 border border-purple-300/30 rounded-lg p-4 mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">Team Progress</span>
                      <span className="text-white font-semibold">{task.progress}%</span>
                    </div>
                    <div className="w-full bg-dark-300 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-purple-200 to-purple-300 h-3 rounded-full transition-all"
                        style={{ width: `${task.progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-purple-200 mt-2">
                      Calculated from {subtasks.length} subtask{subtasks.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Link to Parent Task (if this is a subtask) */}
          {task.parentTask && (
            <div className="border-t border-dark-300 pt-6 mb-6">
              <Link
                to={`/task/${task.parentTask._id || task.parentTask}`}
                className="inline-flex items-center space-x-2 text-purple-200 hover:text-purple-100 transition-colors"
              >
                <FiArrowLeft />
                <span>Back to Parent Task: {task.parentTask.title || 'View Parent Task'}</span>
              </Link>
            </div>
          )}

          {canEdit && (
            <div className="border-t border-dark-300 pt-6">
              <h3 className="font-medium text-white mb-3">Update Status</h3>
              <div className="flex flex-wrap gap-2">
                {['pending', 'in-progress', 'completed', 'on-hold'].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    disabled={updating || task.status === status}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      task.status === status
                        ? 'bg-purple-300 text-white cursor-not-allowed'
                        : 'bg-dark-300 text-dark-100 hover:bg-purple-300/20 hover:text-purple-200'
                    } disabled:opacity-50`}
                  >
                    {status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetails;

